const STORE_URL = "https://happyboxstore.com";
const MAIN_PRODUCT_TYPE = "Box";
const ADDON_PRODUCT_TYPE = "Gift";
const CARD_PRODUCT_TYPE = "Card";

let cartItems = null;

/* =========================
   Small utilities
   ========================= */
function toNumber(val, fallback = 0) {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
}

function ensureArray(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.length) return value.split(",").map(s => s.trim());
    return [];
}

function safeProductTags(product) {
    // Ensure product.tags is always an array, even if it doesn't exist
    if (!product.tags) {
        product.tags = [];
    } else {
        // Mutates product.tags to always be an array
        product.tags = ensureArray(product.tags);
    }
    return product.tags;
}

function safeImagesArray(product) {
    return Array.isArray(product?.images) ? product.images : [];
}

/* =========================
   Cart processing
   ========================= */
function processCartItems(items = []) {
    // Expand GIST bundle to individual variant entries
    const expandBundle = ({ properties = {}, quantity = 0 }) => {
        const rawIds = properties._gs_bundle_ids;
        const rawQtys = properties._gs_bundle_quantities;

        const ids = Array.isArray(rawIds) ? rawIds : String(rawIds || "").split(",");
        const qtys = Array.isArray(rawQtys) ? rawQtys : String(rawQtys || "").split(",");

        return ids
            .map(s => s.trim())
            .filter(Boolean)
            .map((id, i) => ({
                id,
                quantity: (parseInt(qtys[i], 10) || 0) * (parseInt(quantity, 10) || 0)
            }));
    };

    // Flatten to {id, quantity}
    const flat = items.flatMap(item =>
        item?.product_type === "GIST_BUILDER_PRODUCT"
            ? expandBundle(item)
            : [{ id: item?.variant_id, quantity: toNumber(item?.quantity, 0) }]
    );

    // Group by id and sum quantities
    const grouped = flat.reduce((acc, { id, quantity }) => {
        if (!id) return acc;
        acc[id] = (acc[id] || 0) + toNumber(quantity, 0);
        return acc;
    }, {});

    // Convert back to array
    return Object.entries(grouped).map(([id, quantity]) => ({ id, quantity }));
}

function processCart(context, data = {}) {
    const cartQty = toNumber(data?.item_count, 0);
    const items = Array.isArray(data?.items) ? data.items : [];

    // Update cart qty
    context.commit("setCartQty", cartQty);

    // Extract box names from properties.boxName
    const boxNames = items.map(({ properties }) => properties?.boxName).filter(Boolean);

    if (boxNames.length) {
        context.commit("setCartNames", boxNames);
    }

    // Keep a compact list for inventory deduction
    cartItems = processCartItems(items);
}

/* =========================
   Inventory helpers
   ========================= */
function applyVariantInventoryDeductions(variant) {
    const cart = cartItems?.find(
        x => /* loose compare to handle string/number ids */ x.id == variant.id
    );
    let qty = toNumber(variant.inventory_quantity, 0);

    if (cart) qty -= toNumber(cart.quantity, 0);

    const isContinue = variant.inventory_policy == "continue"; // intentional loose compare
    variant.inventory_quantity = isContinue ? 9999 : qty;
    variant.available = toNumber(variant.inventory_quantity, 0) > 0;
    return variant;
}

/* =========================
   Product page processors
   ========================= */
function extractCapacityFromTags(product) {
    // Tags are already normalized by safeProductTags, so we can use them directly
    const capacityTag = product.tags.find(tag => tag.indexOf("Capacity_") > -1);
    if (capacityTag) {
        const capacity = parseFloat(capacityTag.split("_")[1]);
        product.capacity = Number.isFinite(capacity) ? capacity : undefined;
    }
}

function processAddonVariant(variant, product) {
    applyVariantInventoryDeductions(variant);
    variant.sku = variant.sku || "";
    const imgs = safeImagesArray(product);
    // Guard against undefined images to avoid `find` on undefined
    variant.featured_image = imgs.find(img => img?.id == variant.image_id) || null;
    return variant;
}

function filterMainProducts(products) {
    return products.filter(product => {
        if (product.product_type !== MAIN_PRODUCT_TYPE) return false;

        const hasInventory =
            Array.isArray(product.variants) &&
            product.variants.some(
                x => toNumber(x.inventory_quantity, 0) > 0 || x.inventory_policy == "continue"
            );

        if (hasInventory) {
            // Normalize tags first
            safeProductTags(product);

            product.maxCapacity = parseInt(product?.variants?.[0]?.option1, 10);
            product.variants = product.variants.map(applyVariantInventoryDeductions);
            return true;
        }

        // Handle "custom" boxes as always available
        if (
            String(product.title || "")
                .toLowerCase()
                .includes("custom")
        ) {
            // Normalize tags for custom boxes too
            safeProductTags(product);

            product.variants = (product.variants || []).map(v => ({
                ...v,
                available: true,
                inventory_quantity: 999
            }));
            return true;
        }

        return false;
    });
}

function filterAddonProducts(products) {
    return products.filter(product => {
        if (product.product_type !== ADDON_PRODUCT_TYPE) return false;

        const hasValidVariants =
            Array.isArray(product.variants) &&
            product.variants.some(
                x =>
                    Number(x.price) > 0 &&
                    (toNumber(x.inventory_quantity, 0) > 0 || x.inventory_policy == "continue")
            );

        if (!hasValidVariants) return false;

        // Normalize tags and ensure they're assigned back to the product
        safeProductTags(product);

        // Extract capacity
        extractCapacityFromTags(product);

        // Check if on sale
        if (product.variants.some(x => x.compare_at_price != null)) {
            product.tags.push("Filter_Category_Sale");
        }

        // Keep only priced variants and process
        product.variants = product.variants
            .filter(x => parseFloat(x.price) > 0)
            .map(v => processAddonVariant(v, product));

        return Array.isArray(product.variants) && product.variants.length > 0;
    });
}

function filterCardProducts(products) {
    return products
        .filter(
            product =>
                product.product_type === CARD_PRODUCT_TYPE &&
                Array.isArray(product.variants) &&
                product.variants.some(
                    x => toNumber(x.inventory_quantity, 0) > 0 || x.inventory_policy == "continue"
                )
        )
        .map(product => {
            // Normalize tags and ensure they're assigned back to the product
            safeProductTags(product);

            product.isVertical = product.tags.includes("Vertical");
            return product;
        });
}

function updateStoreIncrementally(context, mainProducts, addonProducts, cardProducts) {
    const existingMain = Array.isArray(context.getters?.boxProducts)
        ? context.getters.boxProducts
        : [];
    const existingAddons = Array.isArray(context.getters?.addonProducts)
        ? context.getters.addonProducts
        : [];
    const existingCards = Array.isArray(context.getters?.cardProducts)
        ? context.getters.cardProducts
        : [];

    // Update max capacity from available main variants
    const capacityList = mainProducts
        .flatMap(x => (Array.isArray(x.variants) ? x.variants : []))
        .filter(x => x?.available)
        .map(x => parseInt(x?.option1, 10))
        .filter(n => Number.isFinite(n));

    const maxCapacity = capacityList.length ? Math.max(...capacityList) : 0;
    const currentMax = toNumber(context.getters?.maxCapacity, 0);
    if (maxCapacity > currentMax) {
        context.commit("setMaxCapacity", maxCapacity);
    }

    // Incremental merges (clone to avoid mutation warnings)
    context.commit(
        "setMainProducts",
        JSON.parse(JSON.stringify(existingMain.concat(mainProducts)))
    );
    context.commit(
        "setAddonProducts",
        JSON.parse(JSON.stringify(existingAddons.concat(addonProducts)))
    );
    context.commit(
        "setCardProducts",
        JSON.parse(JSON.stringify(existingCards.concat(cardProducts)))
    );
}

function extractOccasions(context, addonProductList = []) {
    const FILTER_PREFIX = "Filter_";
    const VIEW_ALL_TAG = `${FILTER_PREFIX}Category_View All`;

    // Tags are now normalized by safeProductTags in filter functions, but keep fallback for safety
    const allTags = addonProductList.flatMap(p => ensureArray(p.tags));
    const uniqueTags = [...new Set(allTags.filter(tag => tag.startsWith(FILTER_PREFIX)))];

    if (uniqueTags.length === 0) return;

    // Turn tag "Filter_Category_Sale" into { category: "Category", title: "Sale", tag: "Filter_Category_Sale" }
    const filters = uniqueTags
        .map(tag => {
            const rest = tag.slice(FILTER_PREFIX.length); // e.g., "Category_Sale"
            const [category, title] = rest.split("_");
            return category && title ? { category, title, tag } : null;
        })
        .filter(Boolean);

    // Group by category
    const grouped = filters.reduce((acc, { category, title, tag }) => {
        if (!acc[category]) acc[category] = { category, filters: [] };
        acc[category].filters.push({ title, tag });
        return acc;
    }, {});

    // Commit "Occasion" filters
    if (grouped.Occasion) {
        context.commit("setOccasions", grouped.Occasion.filters);
    }

    // Sort & commit "Category" filters
    if (grouped.Category) {
        const priority = {
            "Corporate Gifts": 5,
            "Snacks & Treats": 4,
            "Brandable Swag": 3,
            "Tea/Coffee": 2,
            "Candles": 1,
            "Sale": 0
        };

        grouped.Category.filters.sort((a, b) => {
            if (a.title === "Sale") return 1;
            if (b.title === "Sale") return -1;
            return (priority[b.title] || 0) - (priority[a.title] || 0);
        });

        // Prepend the "View All"
        grouped.Category.filters.unshift({
            title: "View All",
            tag: VIEW_ALL_TAG
        });

        context.commit("setCategories", grouped.Category);
    }
}

function processProductPage(context, products = []) {
    const mainProducts = filterMainProducts(products);
    const addonProducts = filterAddonProducts(products);
    const cardProducts = filterCardProducts(products);

    updateStoreIncrementally(context, mainProducts, addonProducts, cardProducts);

    // Recompute occasions from the up-to-date addon products in state
    extractOccasions(context, context.state?.addonProducts || []);
}

async function fetchProductsIncrementally(context) {
    let nextPage = null;
    let hasMoreProducts = true;
    let url = "https://api.happyboxstore.com/api/bundle/products";

    while (hasMoreProducts) {
        try {
            const response = await fetch(url);
            const data = await response.json();

            processProductPage(context, Array.isArray(data?.products) ? data.products : []);

            nextPage = data?.next;
            if (!nextPage) {
                hasMoreProducts = false;
            } else {
                url = `https://api.happyboxstore.com/api/bundle/products?page=${nextPage}`;
            }
        } catch (error) {
            console.error("Failed to fetch products page:", error);
            hasMoreProducts = false;
        }
    }
}

/* =========================
   Saved/Edit cart + URL
   ========================= */
function loadEditCart(context, addonProducts, mainProducts, cardProducts) {
    if (!context || typeof context.commit !== "function") {
        console.error("Invalid context provided to loadEditCart");
        return;
    }

    try {
        const editBox = JSON.parse(localStorage.getItem("editBox"));
        if (!editBox || !editBox.ids || !editBox.qtys) {
            console.warn("No valid editBox data found in localStorage");
            return;
        }

        // Ensure arrays are provided
        addonProducts = Array.isArray(addonProducts) ? addonProducts : [];
        mainProducts = Array.isArray(mainProducts) ? mainProducts : [];
        cardProducts = Array.isArray(cardProducts) ? cardProducts : [];

        let filteredProducts = [];

        for (let [i, id] of editBox.ids.entries()) {
            if (!id) continue;

            // Find addon product
            let product = addonProducts.find(x => x.variants && x.variants.some(y => y.id == id));
            if (product) {
                const productCopy = JSON.parse(JSON.stringify(product));
                productCopy.quantity = editBox.qtys[i] || 1;
                filteredProducts.push(productCopy);
            }

            // Find main product (box)
            let box = mainProducts.find(x => x.variants && x.variants.some(y => y.id == id));
            if (box) {
                const boxCopy = JSON.parse(JSON.stringify(box));
                boxCopy.variants = boxCopy.variants.filter(x => x.id == id);
                context.commit("updateSelectedMainProduct", boxCopy);
            }

            // Find card product
            let card = cardProducts.find(x => x.variants && x.variants.some(y => y.id == id));
            if (card) {
                const cardCopy = JSON.parse(JSON.stringify(card));
                context.commit("updateSavedCardProduct", {
                    card: cardCopy,
                    from: editBox.from || "",
                    message: editBox.message || ""
                });
                context.commit("updateSelectedCardProduct", cardCopy);
            }
        }

        if (filteredProducts.length > 0) {
            context.commit("updateAllSelectedAddonProducts", filteredProducts);
            localStorage.removeItem("totalAddons");
        }

        if (editBox.key) {
            context.commit("updateSavedKey", editBox.key);
        }
        if (editBox.boxName && editBox.boxName !== "") {
            context.commit("setBoxName", editBox.boxName);
        }
        if (editBox.boxQty && editBox.boxQty > 0) {
            context.commit("setBoxQty", editBox.boxQty);
        }

        localStorage.removeItem("editBox");
    } catch (error) {
        console.error("Error loading edit cart:", error);
        localStorage.removeItem("editBox");
    }
}

function loadSavedCart(context, addonProducts) {
    if (!context || typeof context.commit !== "function") {
        console.error("Invalid context provided to loadSavedCart");
        return;
    }

    try {
        // Ensure addonProducts is an array
        addonProducts = Array.isArray(addonProducts) ? addonProducts : [];

        const savedProducts = JSON.parse(localStorage.getItem("totalAddons"));
        if (!savedProducts || !Array.isArray(savedProducts)) {
            return;
        }

        // Filter products that exist in addonProducts and have sufficient inventory
        const filteredProducts = addonProducts
            .filter(product => {
                const savedProduct = savedProducts.find(y => y.id == product.id);
                if (!savedProduct) return false;

                // Check if product has variants and sufficient inventory
                return (
                    product.variants &&
                    product.variants[0] &&
                    (product.variants[0].inventory_quantity === null ||
                        product.variants[0].inventory_quantity >= savedProduct.q)
                );
            })
            .map(product => {
                const savedProduct = savedProducts.find(y => y.id == product.id);
                const productCopy = JSON.parse(JSON.stringify(product));
                productCopy.quantity = savedProduct.q || 1;
                return productCopy;
            });

        if (filteredProducts.length > 0) {
            context.commit("updateAllSelectedAddonProducts", filteredProducts);
            localStorage.removeItem("totalAddons");
        }
    } catch (error) {
        console.error("Error loading saved cart:", error);
        localStorage.removeItem("totalAddons");
    }
}

function processUrlParameters(context) {
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of params) {
        if (key === "occasion" && value) {
            context.commit("setSelectedOccasion", value);
        }
        if (key === "product" && value) {
            const product = (context.state?.addonProducts || []).find(x => x.id == value);
            if (product) {
                context.commit("updateSelectedAddonProductsNew", {
                    product,
                    quantity: 1
                });
            }
        }
    }
}

function handlePostProcessing(context) {
    const savedProducts = (() => {
        try {
            return JSON.parse(localStorage.getItem("totalAddons"));
        } catch {
            return null;
        }
    })();

    const editBox = (() => {
        try {
            return JSON.parse(localStorage.getItem("editBox"));
        } catch {
            return null;
        }
    })();

    if (editBox) {
        loadEditCart(
            context,
            context.state?.addonProducts || [],
            context.state?.mainProducts || [],
            context.state?.cardProducts || []
        );
    } else if (savedProducts) {
        loadSavedCart(context, context.state?.addonProducts || []);
    }

    processUrlParameters(context);
    context.commit("setIsLoading", false);
}

async function getCartInfo(context) {
    try {
        const response = await fetch(`${STORE_URL}/cart.js`);
        const cart = await response.json();
        processCart(context, cart);
    } catch (error) {
        console.error("Failed to get cart info:", error);
    }
}

/* =========================
   Vuex actions
   ========================= */
const actions = {
    async getFeeProduct(context) {
        try {
            const response = await fetch("/products/handpacking-fee-5.js");
            const data = await response.json();
            context.commit("setFeeProduct", data);
        } catch (error) {
            console.error("Failed to fetch fee product:", error);
        }
    },

    async getProducts(context) {
        try {
            const savedProducts = (() => {
                try {
                    return JSON.parse(localStorage.getItem("totalAddons"));
                } catch {
                    return null;
                }
            })();

            const editBox = (() => {
                try {
                    return JSON.parse(localStorage.getItem("editBox"));
                } catch {
                    return null;
                }
            })();
            if (savedProducts || editBox) {
                context.commit("setIsLoading", true);
            }
            // 1) Get cart info first
            await getCartInfo(context);
            // 2) Fetch products with incremental processing
            await fetchProductsIncrementally(context);
            // 3) Handle saved/edit cart and URL parameters
            handlePostProcessing(context);
        } catch (error) {
            console.error("Failed to get products:", error);
        }
    }
};

export default actions;
